import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { cn } from '@v2/shared/lib/utils';
import SourceLabel from './SourceLabel';
import Stars from './Stars';
import { Review, ReviewSource } from './types';

type Props = {
  review: Review;
  source: ReviewSource;
};

/**
 * One review of us.
 *
 * Page one of these is drawn by the server, which is the whole point of the rebuild:
 * the widget this replaces injected every review from the browser, so the page that
 * ranks for "discountmags reviews" contained no review text at all in its source.
 *
 * The reviewer is a first name and a last initial and nothing else. The API returns an
 * email address alongside it on solicited reviews; it is dropped in the normalizer and
 * never reaches this component.
 */
export default function ReviewCard({ review, source }: Props) {
  return (
    <article className="rounded-xl border border-solid border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-3 flex items-center gap-3">
        <span
          aria-hidden
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            'bg-primary-50 text-sm font-bold text-primary'
          )}
        >
          {review.initials}
        </span>

        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{review.author}</div>
          <div className="text-xs text-slate-500">
            {review.publishedLabel && (
              <>
                <time dateTime={review.publishedAt ?? undefined}>{review.publishedLabel}</time>
                {' · '}
              </>
            )}
            <SourceLabel name={source.name} />
          </div>
        </div>

        {review.verifiedBuyer && (
          <span
            className={cn(
              'ml-auto flex items-center gap-1.5 whitespace-nowrap rounded-full',
              'bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700'
            )}
          >
            <FontAwesomeIcon icon={faCheck} aria-hidden /> Verified buyer
          </span>
        )}
      </div>

      <Stars rating={review.rating} className="mb-1.5" />

      {review.title && <h3 className="mb-1 text-base font-bold text-slate-900">{review.title}</h3>}

      {review.body && <p className="m-0 text-sm text-slate-600">{review.body}</p>}

      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        {review.dateOfExperienceLabel && <span>Date of experience: {review.dateOfExperienceLabel}</span>}

        {review.helpfulVotes > 0 && (
          <span className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faThumbsUp} aria-hidden />
            {review.helpfulVotes.toLocaleString('en-US')} found this helpful
          </span>
        )}
      </div>
    </article>
  );
}
